import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { cx } from "@nju/css";
import {
  IRoamingInternationalCountry,
  IZone,
} from "@nju/data-access-portal-dashboard";
import { portalRoutes } from "@nju/routes";
import { CircleLoader, GeneralErrorNotification } from "@nju/ui";
import { Buttons, ChooseCountry, Offer, Sidebar } from "./partials";
import { selectOfferMachine } from "./selectOffer.machine";
import { IButtonType, IPackageType } from "./types";
import { ZERO_OFFER_ID } from "./utils";

export function SelectOfferPage() {
  const [packageType, setPackageType] = React.useState<IPackageType>("PROMO");
  const router = useRouter();
  const params = portalRoutes.mojeKonto.uslugiDodatkowe.roaming.index
    .input(router.query)
    .unwrap();
  const country = params?.country || "";
  const { userId } = portalRoutes.mojeKonto.uslugiDodatkowe.index
    .input(router.query)
    .unwrap();

  const [current, send] = useMachine(selectOfferMachine, {
    actions: {
      onSubmitSuccess: (context, event) => {
        if (
          event.type === "SUBMIT" &&
          context.selectedCountry &&
          context.offerType !== "EU"
        ) {
          const voicePackageId =
            context.selectedVoicePackage?.id !== ZERO_OFFER_ID
              ? context.selectedVoicePackage?.id
              : undefined;
          const dataPackageId =
            context.selectedDataPackage?.id !== ZERO_OFFER_ID
              ? context.selectedDataPackage?.id
              : undefined;

          router.push(
            portalRoutes.mojeKonto.uslugiDodatkowe.roaming.podsumowanie.get({
              country: context.selectedCountry.displayName.pl,
              zoneName: context.selectedCountry.zones.find((zone) => {
                return zone.name === context.zone;
              })?.displayName.pl,
              zone: context.zone as IZone["name"],
              ...(userId && { userId }),
              ...(voicePackageId && {
                voicePackageId,
              }),
              ...(dataPackageId && {
                dataPackageId,
              }),
            }),
          );
        }

        if (
          event.type === "SUBMIT" &&
          context.selectedCountry &&
          context.offerType === "EU"
        ) {
          router.push(
            portalRoutes.mojeKonto.uslugiDodatkowe.pakietGb.get({
              ...(userId && { userId }),
            }),
          );
        }
      },
    },
    context: {
      countrySearch: country,
    },
  });

  const { context, matches } = current;

  const {
    filteredCountries,
    selectedCountry,
    formError,
    roamingOffers,
    promoRoamingOffers,
    userFunds,
    price,
    isNotEnoughFunds,
    packageGb,
    selectedDataPackage,
    selectedVoicePackage,
    offerType,
    countrySearch,
    zeroOfferError,
  } = context;

  const handleSetCountrySearch = React.useCallback(
    (search: string) => {
      send({ type: "SET_COUNTRY_SEARCH", search });
    },
    [send],
  );

  const handleSubmit = React.useCallback(() => {
    send({ type: "SUBMIT" });
  }, [send]);

  const handleSelectCountry = React.useCallback(
    (selectedCountries: IRoamingInternationalCountry[]) => {
      if (selectedCountries[0]) {
        send({ type: "SELECT_COUNTRY", selectedCountry: selectedCountries[0] });
        setPackageType("PROMO");
      }
    },
    [send],
  );
  const handleClearCountry = React.useCallback(() => {
    send({ type: "CLEAR_SEARCH_COUNTRY" });
  }, [send]);

  const handleSelectVoicePackage = React.useCallback(
    (buttonType: IButtonType, packageType: IPackageType) => {
      send({ type: "SELECT_VOICE_PACKAGE", buttonType, packageType });
    },
    [send],
  );

  const handleSelectDataPackage = React.useCallback(
    (buttonType: IButtonType, packageType: IPackageType) => {
      send({ type: "SELECT_DATA_PACKAGE", buttonType, packageType });
    },
    [send],
  );

  const handleSetDefaultPackages = React.useCallback(
    (packageType: IPackageType) => {
      send({ type: "SET_DEFAULT_PACKAGES", packageType });
    },
    [send],
  );

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return router.reload();
        }}
      />
    );
  }

  if (matches("loading")) {
    return (
      <div className="flex justify-center items-center h-full">
        <CircleLoader />
      </div>
    );
  }

  return (
    <div
      className={cx(
        "flex from-tablet:gap-12 flex-col only-mobile:flex-col-reverse from-tablet:flex-row",
        {
          "only-mobile:gap-5": matches({ countrySelected: "ready" }),
          "only-mobile:gap-0": matches("ready"),
        },
      )}
    >
      <div className="flex-[2] mt-2">
        <ChooseCountry
          filteredCountries={filteredCountries}
          formError={formError}
          handleClearCountry={handleClearCountry}
          handleSelectCountry={handleSelectCountry}
          inputValue={countrySearch}
          isCountrySelected={matches({ countrySelected: "ready" })}
          isLoading={matches("loading")}
          selectedCountry={selectedCountry}
          setInputValue={handleSetCountrySearch}
          userId={userId}
        />
        {matches({ countrySelected: "ready" }) && selectedCountry && (
          <Offer
            data-cy="offer"
            offerType={offerType}
            onSelectDataPackage={handleSelectDataPackage}
            onSelectVoicePackage={handleSelectVoicePackage}
            onSetDefaultPackages={handleSetDefaultPackages}
            packageType={packageType}
            promoRoamingOffers={promoRoamingOffers}
            roamingOffers={roamingOffers}
            selectedCountry={selectedCountry}
            selectedDataPackage={selectedDataPackage}
            selectedVoicePackage={selectedVoicePackage}
            setPackageType={setPackageType}
          />
        )}
        <Buttons
          isLoading={matches("success")}
          isNotEnoughFunds={isNotEnoughFunds}
          onSubmit={handleSubmit}
          zeroOfferError={zeroOfferError}
        />
      </div>
      <div className="flex-[1] flex justify-center from-desktop:block">
        <Sidebar
          dataPackageAnniversaryDate={selectedDataPackage?.refillPeriod}
          isCountrySelected={matches({ countrySelected: "ready" })}
          isNotEnoughFunds={isNotEnoughFunds}
          offerType={offerType}
          packageGb={packageGb}
          price={price}
          userFunds={userFunds}
          voicePackageAnniversaryDate={selectedVoicePackage?.refillPeriod}
        />
      </div>
    </div>
  );
}
